import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import Pagination from '../components/ui/Pagination';
import FilterBar from '../components/ui/FilterBar';
import { apiService } from '../services/api';
import { useDebounce } from '../hooks/useDebounce';
import { useAuth } from '../hooks/useAuth';
import { 
  Users as UsersIcon, 
  Wifi,
  Clock,
  Globe,
  Monitor,
  Smartphone,
  Tablet,
  Tv,
  RefreshCw,
  Eye,
  MapPin,
  User,
  Zap
} from 'lucide-react';

interface OnlineClient {
  id: number;
  user_id: number;
  username: string;
  created_at: string;
  watching: string;
  master_username: string;
  reseller_username: string;
  server_time: number;
  user_agent: string;
  user_ip: string;
  container: string;
  date_start: number;
  geoip_country_code: string;
  system: number;
  p2p_id: string;
  p2p_status: string;
  max_connections: number;
  is_trial: number;
  duration_online: number;
  duration_online_formatted: string;
}

interface OnlineClientsStats {
  total: number;
  online: number;
  iptv: number;
  p2p: number;
  hybrid: number;
  trial: number;
}

const OnlineClients: React.FC = () => {
  const { isAdmin } = useAuth();
  // Estados principais
  const [clients, setClients] = useState<OnlineClient[]>([]);
  const [stats, setStats] = useState<OnlineClientsStats>({
    total: 0,
    online: 0,
    iptv: 0,
    p2p: 0,
    hybrid: 0,
    trial: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalClients, setTotalClients] = useState(0);

  // Estados de busca e filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [systemFilter, setSystemFilter] = useState('');
  const [trialFilter, setTrialFilter] = useState('');
  const [resellerFilter, setResellerFilter] = useState(''); // Filtro de revendedor

  // Debounce para busca (500ms de delay)
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Opções para filtros
  const [statusOptions, setStatusOptions] = useState<Array<{value: string, label: string}>>([]);
  const [systemOptions, setSystemOptions] = useState<Array<{value: string, label: string}>>([]);
  const [trialOptions, setTrialOptions] = useState<Array<{value: string, label: string}>>([]);

  // Função para buscar clientes online
  const fetchOnlineClients = useCallback(async () => {
    try {
      // Se for uma busca (não o carregamento inicial), mostrar indicador de busca
      if (debouncedSearchTerm || statusFilter || systemFilter || trialFilter || resellerFilter) {
        setIsSearching(true);
      } else {
        setIsLoading(true);
      }

      const params = new URLSearchParams({
        page: currentPage.toString(),
        per: '15'
      });

      if (debouncedSearchTerm) params.append('search', debouncedSearchTerm);
      if (statusFilter) params.append('status', statusFilter);
      if (systemFilter) params.append('system', systemFilter);
      if (trialFilter) params.append('type_user', trialFilter);
      if (resellerFilter) params.append('owner_id', resellerFilter);

      const response = await apiService.getOnlineClients(params);
      
      if (response.data) {
        // Garantir que os dados estejam mapeados corretamente
        const mappedClients = response.data.map((client: any) => ({
          id: client.id || client.activity_id || 0,
          user_id: client.user_id || 0,
          username: client.username || 'N/A',
          created_at: client.created_at || '',
          watching: client.watching || client.stream_display_name || 'N/A',
          master_username: client.master_username || '',
          reseller_username: client.reseller_username || client.resellerUsername || '',
          server_time: client.server_time || client.serverTime || Date.now(),
          user_agent: client.user_agent || client.userAgent || '',
          user_ip: client.user_ip || client.userIp || '',
          container: client.container || '',
          date_start: client.date_start || client.dateStart || 0,
          geoip_country_code: client.geoip_country_code || client.geoipCountryCode || '',
          system: Number(client.system || 0),
          p2p_id: String(client.p2p_id || client.p2pId || ''),
          // Para clientes P2P online (system === 2), p2p_status sempre deve ser '1'
          // Se system === 1 (IPTV), p2p_status pode ser '0' ou vazio
          p2p_status: Number(client.system) === 2 || Number(client.system) === 3
            ? String(client.p2p_status || client.p2pStatus || '1')
            : String(client.p2p_status || client.p2pStatus || '0'),
          max_connections: Number(client.max_connections || client.maxConnections || 0),
          is_trial: Number(client.is_trial || client.isTrial || 0),
          duration_online: Number(client.duration_online || client.durationOnline || 0),
          duration_online_formatted: client.duration_online_formatted || client.durationOnlineFormatted || '0h 0m'
        }));
        
        setClients(mappedClients);
        setTotalPages(response.pagination?.total_pages || 1);
        const total = response.pagination?.total || 0;
        setTotalClients(total);

        // Estatísticas globais vindas do backend
        if (response.stats) {
          setStats({
            total: total,
            online: response.stats.online ?? total,
            iptv: response.stats.iptv ?? 0,
            p2p: response.stats.p2p ?? 0,
            hybrid: response.stats.hybrid ?? 0,
            trial: response.stats.trial ?? 0
          });
        }
      }
    } catch (error) {
      console.error('Erro ao buscar clientes online:', error);
    } finally {
      setIsLoading(false);
      setIsSearching(false);
    }
  }, [currentPage, debouncedSearchTerm, statusFilter, systemFilter, trialFilter, resellerFilter]);

  // Função para buscar estatísticas
  const fetchStats = useCallback(async () => {
    try {
      // Apenas ajustar total/online com base no total global
      setStats(prev => ({
        ...prev,
        total: totalClients,
        online: totalClients
      }));
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
    }
  }, [totalClients]);

  // Carregar opções dos filtros
  const loadFilterOptions = useCallback(async () => {
    try {
      // Definir opções fixas
      setStatusOptions([
        { value: 'online', label: 'Online' },
        { value: 'offline', label: 'Offline' }
      ]);

      setSystemOptions([
        { value: '1', label: 'IPTV' },
        { value: '2', label: 'P2P' },
        { value: '3', label: 'Hibrido-CMS' }
      ]);

      setTrialOptions([
        { value: 'trial', label: 'Trial' },
        { value: 'official', label: 'Oficial' }
      ]);
    } catch (error) {
      console.error('Erro ao carregar opções de filtros:', error);
    }
  }, []);

  // Efeitos
  useEffect(() => {
    fetchOnlineClients();
  }, [fetchOnlineClients]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  // Função de busca
  const handleSearch = () => {
    setCurrentPage(1);
    fetchOnlineClients();
  };

  // Função para limpar filtros
  const clearFilters = () => {
    setStatusFilter('');
    setSystemFilter('');
    setTrialFilter('');
    setResellerFilter('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Função para detectar tipo de dispositivo baseado no user agent
  const getDeviceType = (userAgent: string) => {
    const ua = userAgent.toLowerCase();
    
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
      return { type: 'mobile', icon: Smartphone, color: 'text-blue-600' };
    } else if (ua.includes('tablet') || ua.includes('ipad')) {
      return { type: 'tablet', icon: Tablet, color: 'text-green-600' };
    } else if (ua.includes('tv') || ua.includes('smart-tv') || ua.includes('roku') || ua.includes('chromecast')) {
      return { type: 'tv', icon: Tv, color: 'text-purple-600' };
    } else if (ua.includes('kodi') || ua.includes('vlc') || ua.includes('mx player')) {
      return { type: 'player', icon: Monitor, color: 'text-orange-600' };
    } else {
      return { type: 'desktop', icon: Monitor, color: 'text-gray-600' };
    }
  };

  // Função para obter status do sistema
  const getSystemStatus = (system: number, p2pStatus: string | number) => {
    if (system === 1) {
      return { label: 'IPTV', color: 'bg-green-100 text-green-800' };
    } else if (system === 2) {
      const statusStr = String(p2pStatus || '');
      const isActive = statusStr === '1' || statusStr === 'true' || statusStr === 'active';
      const status = isActive ? 'Ativo' : 'Inativo';
      return { label: `P2P (${status})`, color: 'bg-purple-100 text-purple-800' };
    } else if (system === 3) {
      return { label: 'Hibrido-CMS', color: 'bg-teal-100 text-teal-800' };
    }
    return { label: 'Desconhecido', color: 'bg-gray-100 text-gray-800' };
  };

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Wifi className="h-6 w-6 text-green-600" />
            Clientes Online
          </h1>
          <p className="text-gray-600">
            {isAdmin() ? 'Monitorar clientes conectados em tempo real' : 'Exibindo apenas clientes da sua árvore'}
          </p>
        </div>
        <Button 
          onClick={fetchOnlineClients}
          disabled={isLoading}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-4">
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 hover:shadow-lg transition-all duration-200">
          <CardContent className="p-3 lg:p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm lg:text-lg font-medium text-green-700 truncate">Total Online</p>
                <p className="text-lg sm:text-xl lg:text-3xl font-bold text-green-900">{stats.online.toLocaleString()}</p>
              </div>
              <div className="p-2 lg:p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-full shadow-lg flex-shrink-0">
                <Wifi className="h-4 w-4 lg:h-6 lg:w-6 text-white drop-shadow-sm" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 hover:shadow-lg transition-all duration-200">
          <CardContent className="p-3 lg:p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm lg:text-lg font-medium text-blue-700 truncate">IPTV</p>
                <p className="text-lg sm:text-xl lg:text-3xl font-bold text-blue-900">{stats.iptv.toLocaleString()}</p>
              </div>
              <div className="p-2 lg:p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full shadow-lg flex-shrink-0">
                <Monitor className="h-4 w-4 lg:h-6 lg:w-6 text-white drop-shadow-sm" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 hover:shadow-lg transition-all duration-200">
          <CardContent className="p-3 lg:p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm lg:text-lg font-medium text-purple-700 truncate">Usuário online P2P</p>
                <p className="text-lg sm:text-xl lg:text-3xl font-bold text-purple-900">{stats.p2p.toLocaleString()}</p>
              </div>
              <div className="p-2 lg:p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full shadow-lg flex-shrink-0">
                <Globe className="h-4 w-4 lg:h-6 lg:w-6 text-white drop-shadow-sm" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200 hover:shadow-lg transition-all duration-200">
          <CardContent className="p-3 lg:p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm lg:text-lg font-medium text-teal-700 truncate">Hibrido-CMS</p>
                <p className="text-lg sm:text-xl lg:text-3xl font-bold text-teal-900">{stats.hybrid.toLocaleString()}</p>
              </div>
              <div className="p-2 lg:p-3 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full shadow-lg flex-shrink-0">
                <Zap className="h-4 w-4 lg:h-6 lg:w-6 text-white drop-shadow-sm" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 hover:shadow-lg transition-all duration-200">
          <CardContent className="p-3 lg:p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm lg:text-lg font-medium text-orange-700 truncate">Trial</p>
                <p className="text-lg sm:text-xl lg:text-3xl font-bold text-orange-900">{stats.trial.toLocaleString()}</p>
              </div>
              <div className="p-2 lg:p-3 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full shadow-lg flex-shrink-0">
                <Clock className="h-4 w-4 lg:h-6 lg:w-6 text-white drop-shadow-sm" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200 hover:shadow-lg transition-all duration-200">
          <CardContent className="p-3 lg:p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm lg:text-lg font-medium text-indigo-700 truncate">Total</p>
                <p className="text-lg sm:text-xl lg:text-3xl font-bold text-indigo-900">{stats.total.toLocaleString()}</p>
              </div>
              <div className="p-2 lg:p-3 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-full shadow-lg flex-shrink-0">
                <UsersIcon className="h-4 w-4 lg:h-6 lg:w-6 text-white drop-shadow-sm" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros e Busca */}
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onSearchSubmit={() => handleSearch()}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        statusOptions={statusOptions}
        systemFilter={systemFilter}
        onSystemFilterChange={setSystemFilter}
        systemOptions={systemOptions}
        trialFilter={trialFilter}
        onTrialFilterChange={setTrialFilter}
        trialOptions={trialOptions}
        resellerFilter={resellerFilter}
        onResellerFilterChange={setResellerFilter}
        onClearFilters={clearFilters}
        showAdvancedFilters={true}
      />

      {/* Lista de Clientes Online */}
      <Card className="shadow-sm">
        <CardHeader className="bg-gray-50 border-b">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                Clientes Conectados
                {isSearching && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                )}
              </CardTitle>
              <CardDescription className="mt-1">
                {totalClients.toLocaleString()} clientes online
                {isSearching && " (buscando...)"}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="text-gray-500">Carregando clientes online...</p>
              </div>
            </div>
          ) : clients.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Wifi className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum cliente online</h3>
                <p className="text-gray-500">Não há clientes conectados no momento.</p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-2 lg:p-4 font-semibold text-gray-700 text-sm lg:text-lg">Cliente</th>
                    <th className="text-left p-2 lg:p-4 font-semibold text-gray-700 text-sm lg:text-lg hidden sm:table-cell">Dispositivo</th>
                    <th className="text-left p-2 lg:p-4 font-semibold text-gray-700 text-sm lg:text-lg hidden md:table-cell">Assistindo</th>
                    <th className="text-left p-2 lg:p-4 font-semibold text-gray-700 text-sm lg:text-lg">Sistema</th>
                    <th className="text-left p-2 lg:p-4 font-semibold text-gray-700 text-sm lg:text-lg hidden lg:table-cell">Tempo Online</th>
                    <th className="text-left p-2 lg:p-4 font-semibold text-gray-700 text-sm lg:text-lg hidden xl:table-cell">IP/Localização</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {clients.map((client) => {
                    const deviceInfo = getDeviceType(client.user_agent);
                    const DeviceIcon = deviceInfo.icon;
                    const systemInfo = getSystemStatus(client.system, client.p2p_status);
                    
                    return (
                      <tr key={client.id} className="hover:bg-gray-50 transition-colors">
                        <td className="p-2 lg:p-4">
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0">
                              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                <User className="h-4 w-4 text-green-600" />
                              </div>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xl font-medium text-gray-900 truncate">
                                {client.username && client.username !== 'N/A' ? client.username : 'Usuário desconhecido'}
                              </p>
                              {client.reseller_username && (
                                <p className="text-sm text-gray-500 truncate">
                                  {client.reseller_username}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="p-2 lg:p-4 hidden sm:table-cell">
                          <div className="flex items-center gap-2">
                            <DeviceIcon className={`h-4 w-4 ${deviceInfo.color}`} />
                            <span className="text-sm text-gray-600 capitalize">
                              {deviceInfo.type}
                            </span>
                          </div>
                        </td>
                        <td className="p-2 lg:p-4 hidden md:table-cell">
                          <div className="flex items-center gap-2">
                            <Eye className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-600 truncate max-w-32">
                              {client.watching || 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td className="p-2 lg:p-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${systemInfo.color}`}>
                            {systemInfo.label}
                          </span>
                        </td>
                        <td className="p-2 lg:p-4 hidden lg:table-cell">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-600">
                              {client.duration_online_formatted}
                            </span>
                          </div>
                        </td>
                        <td className="p-2 lg:p-4 hidden xl:table-cell">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-gray-400" />
                            <div className="text-sm text-gray-600">
                              <div>{client.user_ip}</div>
                              {client.geoip_country_code && (
                                <div className="text-xs text-gray-500">
                                  {client.geoip_country_code}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
};

export default OnlineClients;
